import React, { useEffect, useState } from 'react';
import { Box, BoxProps, Flex, TooltipV2, Theme } from '@withjoy/joykit';
import { TextV2, TextV2Props } from '@withjoy/joykit/components/TextV2';
import { Toggle } from './StyledToggle.styles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { pxToRem } from '@withjoy/joykit/theme';
import { Help } from '@withjoy/joykit/icons';

interface StyledToggleProps {
  label: string;
  name: string;
  sideNote?: string;
  disabled?: boolean;
  isChecked?: boolean;
  tooltip?: {
    title?: string;
    content?: string;
  };
  overrides?: {
    Root?: { props: BoxProps };
    Label?: { props: TextV2Props };
  };
  toggleBgColor?: keyof Theme['colors'];
  onChange?: () => void;
}

const StyledToggle = ({ label, name, sideNote, disabled, isChecked: isCheckedArg, tooltip, overrides, toggleBgColor, onChange }: StyledToggleProps) => {
  const [isChecked, setIsChecked] = useState(isCheckedArg);

  const handleOnChange = useEventCallback(() => {
    setIsChecked(isCheckedPrev => !isCheckedPrev);
    onChange?.();
  });

  useEffect(() => {
    setIsChecked(isCheckedArg);
  }, [isCheckedArg]);

  return (
    <Box {...overrides?.Root?.props}>
      <Toggle
        bgColor={toggleBgColor}
        label={
          <Box>
            <Flex
              width="100%"
              gap={2}
              alignItems="center"
            >
              <TextV2
                typographyVariant="button2"
                color={disabled ? 'mono8' : 'mono14'}
                {...overrides?.Label?.props}
              >
                {label}
              </TextV2>
              {tooltip && (
                <TooltipV2
                  overrides={{
                    Content: { props: { paddingY: 4, paddingLeft: pxToRem(14), paddingRight: 4, borderRadius: pxToRem(12), color: 'white', marginLeft: 2 } },
                  }}
                  content={
                    <Flex
                      flexDirection="column"
                      maxWidth={pxToRem(256)}
                    >
                      <Box typographyVariant="label3">{tooltip.title}</Box>
                      <Box
                        typographyVariant="label2"
                        color="mono4"
                      >
                        {tooltip.content}
                      </Box>
                    </Flex>
                  }
                  placement="top"
                  onMouseEnterDelay={500}
                >
                  <Help
                    size={16}
                    color="mono10"
                  />
                </TooltipV2>
              )}
            </Flex>
            {sideNote && (
              <TextV2
                typographyVariant="label1"
                marginTop={2}
                color={disabled ? 'mono8' : 'mono11'}
                {...overrides?.Label?.props}
              >
                {sideNote}
              </TextV2>
            )}
          </Box>
        }
        disabled={disabled}
        checked={isChecked}
        name={name}
        onChange={handleOnChange}
      />
    </Box>
  );
};

StyledToggle.defaults = {
  overrides: {
    Root: { props: { backgroundColor: 'white', width: '100%' } },
    Label: { props: { typographyVariant: 'button2' } },
  },
};

export default StyledToggle;
